/**
 * The "waiting on others" / "someone responded" filter shared by Triage and
 * Cases (waiting filter round). It lives in the URL query, so a link and the
 * back button keep it:
 *
 *   ?show=waiting               items waiting on someone else
 *   ?show=waiting&who=Cliff%20Platt   …on that one person
 *   ?show=responded             items where someone has answered
 *   ?show=overdue               cases with a Sigma clock or a dated item past due
 *
 * Other query keys a page keeps (Cases' court / stage) are left as they are.
 *
 * Pure logic (repo convention: logic in .ts, components in .tsx).
 */

export type ShowFilter = 'all' | 'waiting' | 'responded' | 'overdue';

export interface ListFilter {
  show: ShowFilter;
  /** One person, only with show = waiting. */
  who: string | null;
}

export const NO_FILTER: ListFilter = { show: 'all', who: null };

export function readListFilter(params: URLSearchParams): ListFilter {
  const show = params.get('show');
  const who = (params.get('who') ?? '').trim() || null;
  if (show === 'responded' || show === 'overdue') return { show, who: null };
  if (show === 'waiting' || who) return { show: 'waiting', who };
  return NO_FILTER;
}

/** The query with this filter set (other keys kept); '' when nothing is left. */
export function withListFilter(params: URLSearchParams, f: ListFilter): string {
  const next = new URLSearchParams(params);
  next.delete('show');
  next.delete('who');
  if (f.show !== 'all') next.set('show', f.show);
  if (f.show === 'waiting' && f.who) next.set('who', f.who);
  const q = next.toString();
  return q ? `?${q}` : '';
}

export interface PersonCount {
  who: string;
  count: number;
}

/** Who is being waited on and on how many items, most first, then by name.
 *  Names are matched loosely (case, spacing) so "cliff platt" and "Cliff
 *  Platt" are one person. */
export function peopleWaitedOn(names: string[][]): PersonCount[] {
  const byKey = new Map<string, PersonCount>();
  for (const itemNames of names) {
    const seen = new Set<string>();
    for (const raw of itemNames) {
      const who = raw.trim();
      const key = personKey(who);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      const hit = byKey.get(key);
      if (hit) hit.count += 1;
      else byKey.set(key, { who, count: 1 });
    }
  }
  return [...byKey.values()].sort((a, b) => b.count - a.count || a.who.localeCompare(b.who));
}

export function personKey(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Is `who` (from the URL) one of these names? */
export function namesInclude(names: string[], who: string): boolean {
  const key = personKey(who);
  return names.some((n) => personKey(n) === key);
}
